import {
  Box,
  Divider,
  Popover,
  SubjectPropertyIcon,
  Typography,
} from "@avenue-8/sidekick-ui";
import {
  NeighborhoodMapProperty,
  NeighborhoodMapSectionView,
  PropertyPhoto,
} from "@cma/presentation-generation-logic";
import { getPropertyStatusColor } from "./helpers/get-property-status-colors";
import { useCallback, useState } from "react";
import { GoogleMap } from "./components/GoogleMap";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { PropertyDetail } from "./components/PropertyDetail";
import FullscreenWrapper from "./components/FullscreenWrapper";
import { blurActiveElement } from "@cma/shared/helpers/blur-active-element";
import { PresentationSectionViewProps } from "../PresentationSectionProps";
import {
  MapLegend,
  BulletIcon,
  MapLegendItem,
  MapContainer,
  MapWrapper,
  FullScreenIconButton,
} from "./NeighborhoodMap.styles";

interface Props
  extends PresentationSectionViewProps<NeighborhoodMapSectionView> {
  galleries?: { [key: string]: PropertyPhoto[] };
}

export const NeighborhoodMap = (props: Props) => {
  const { sectionView, galleries } = props;
  const { id, properties: allProperties } = sectionView;

  const properties = allProperties?.filter((x) => x.lat && x.lng) ?? [];

  const [marker, setMarker] = useState<{
    el: HTMLElement;
    open: boolean;
    properties: Array<{
      property: NeighborhoodMapProperty;
      gallery?: PropertyPhoto[];
    }>;
  } | null>(null);
  const [fullScreenMap, setFullScreenMap] = useState<boolean>(false);

  const onMarkerClick = (
    e: React.MouseEvent<HTMLDivElement>,
    properties: NeighborhoodMapProperty[],
  ) => {
    setMarker({
      el: e.currentTarget,
      open: true,
      properties: properties.map((x) => {
        return { property: x, gallery: galleries?.[x.id] };
      }),
    });
    e.stopPropagation();
    return false;
  };

  const toggleFullscreenMap = useCallback(() => {
    setFullScreenMap(!fullScreenMap);
    blurActiveElement();
  }, [fullScreenMap]);

  return (
    <Box id={id} pt={4} pb={6} px={4} textAlign="center">
      <Typography variant="h5" mb={3}>
        The Neighborhood at a Glance
      </Typography>
      <FullscreenWrapper
        open={fullScreenMap}
        onClose={() => setFullScreenMap(false)}
      >
        <MapContainer data-testid="fullscreen-neighborhood-map-container">
          <FullScreenIconButton onClick={toggleFullscreenMap}>
            <FullscreenIcon />
          </FullScreenIconButton>
          <GoogleMap onMarkerClick={onMarkerClick} properties={properties} />
        </MapContainer>
      </FullscreenWrapper>

      <MapWrapper
        id="inline-neighborhood-map"
        aria-labelledby="inline-neighborhood-map"
      >
        <MapContainer data-testid="neighborhood-map-container">
          <FullScreenIconButton onClick={toggleFullscreenMap}>
            <FullscreenIcon />
          </FullScreenIconButton>
          <GoogleMap onMarkerClick={onMarkerClick} properties={properties} />
        </MapContainer>
      </MapWrapper>

      <MapLegend data-testid="neighborhood-map-legend">
        {Boolean(properties.find((p) => p.type === "subject")) && (
          <MapLegendItem>
            <SubjectPropertyIcon />
            Subject Property
          </MapLegendItem>
        )}
        <MapLegendItem>
          <BulletIcon markerColor={getPropertyStatusColor("comparable-sold")} />
          Comparable Sold
        </MapLegendItem>
        <MapLegendItem>
          <BulletIcon
            markerColor={getPropertyStatusColor("comparable-pending")}
          />
          Comparable Pending
        </MapLegendItem>
        <MapLegendItem>
          <BulletIcon
            markerColor={getPropertyStatusColor("comparable-active")}
          />
          Comparable Active
        </MapLegendItem>
        <MapLegendItem>
          <BulletIcon markerColor={getPropertyStatusColor("other")} />
          Other
        </MapLegendItem>
      </MapLegend>
      <Popover
        open={marker?.open ?? false}
        anchorEl={marker?.el}
        onClose={() => setMarker({ ...(marker as any), open: false })}
        style={{ marginTop: "-5px" }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Box p={2}>
          {marker?.properties.map((m, i) => {
            return (
              <div key={m.property.id}>
                {i > 0 && <Divider sx={{ width: "100%", mt: 2, mb: 3 }} />}
                <PropertyDetail
                  key={m.property.id}
                  gallery={m.gallery}
                  property={m.property}
                />
              </div>
            );
          })}
        </Box>
      </Popover>
    </Box>
  );
};
